import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Tooltip, { TooltipProvider } from "@/components/ui/tooltip";
import type { Review } from "@/modules/review/types";
import IconPin from "@/components/icons/icon-pin";
import IconAnonymous from "@/components/icons/icon-anonymous";

interface PinnedReviewItemProps {
	review: Review;
	onUnpin: () => void;
	draggable?: boolean;
}

// Helper to render star rating
function StarRating({ rating }: { rating: number }) {
	return (
		<div className="flex items-center gap-0.5">
			{[1, 2, 3, 4, 5].map((star) => (
				<svg key={star} className={`w-3 h-3 ${star <= rating ? "text-amber-400" : "text-gray-200"}`} fill="currentColor" viewBox="0 0 20 20">
					<path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
				</svg>
			))}
		</div>
	);
}

export default function PinnedReviewItem({ review, onUnpin, draggable = true }: PinnedReviewItemProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: review.id, disabled: !draggable });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	// Get display content
	const isAnonymous = review.consent === "anonymous";
	const displayName = review.customer_name || "No name";
	const displayContent = review.content_text || "";
	const truncatedContent = displayContent.length > 80 ? displayContent.slice(0, 80) + "..." : displayContent;

	// Generate avatar background gradient if no avatar
	const avatarGradient = `hsl(${(displayName.charCodeAt(0) * 137) % 360}, 70%, 60%)`;

	return (
		<div ref={setNodeRef} style={style} className={`flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 ${isDragging ? "opacity-50 shadow-lg" : ""}`}>
			{/* Drag handle */}
			{draggable && (
				<button type="button" className="shrink-0 p-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" {...attributes} {...listeners}>
					<svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
						<circle cx="9" cy="6" r="1.5" />
						<circle cx="15" cy="6" r="1.5" />
						<circle cx="9" cy="12" r="1.5" />
						<circle cx="15" cy="12" r="1.5" />
						<circle cx="9" cy="18" r="1.5" />
						<circle cx="15" cy="18" r="1.5" />
					</svg>
				</button>
			)}

			{/* Avatar */}
			<div className="shrink-0">
				{review.customer_avatar_url ? (
					<img src={review.customer_avatar_url} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
				) : (
					<div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium" style={{ backgroundColor: avatarGradient }}>
						{displayName.charAt(0).toUpperCase()}
					</div>
				)}
			</div>

			{/* Content */}
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2 mb-0.5">
					<span className={`text-sm font-medium truncate ${isAnonymous ? "italic text-gray-400" : "text-gray-900"}`}>{displayName}</span>
					{isAnonymous && (
						<TooltipProvider>
							<Tooltip content="This review is set to anonymous — personal details will be hidden from the public.">
								<IconAnonymous className="w-3.5 h-3.5 text-gray-400 shrink-0 cursor-help" />
							</Tooltip>
						</TooltipProvider>
					)}
					{review.rating > 0 && <StarRating rating={review.rating} />}
				</div>
				<p className="text-sm text-tertiary truncate">{truncatedContent}</p>
			</div>

			{/* Unpin button */}
			<button type="button" onClick={onUnpin} className="shrink-0 p-1.5 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-50 transition-colors" aria-label="Remove from pinned">
				<IconPin solid />
			</button>
		</div>
	);
}
